import React from "react";
import cn from "classnames";
import { Helmet } from "react-helmet";
import { gtmPageview, gtmPush } from "../../functions/gtm";
import Layout from "../../components/Layout";
import queryString from "query-string";
import Spinner from "../../audi-ui-components/Spinner";
import Button from "../../audi-ui-components/Button";
import Switch from "../../audi-ui-components/Switch";
import _find from "lodash/find";
import _findIndex from "lodash/findIndex";
import _filter from "lodash/filter";
import PostcodeModal from "./components/PostcodeModal";
import EnquiryForm from "./components/EnquiryForm";
import ApplyFormModal from "./components/ApplyFormModal";
import PriceCalculatorComponent from "./components/PriceCalculatorComponent";
import AFSCalculatorComponent from "./components/AFSCalculatorComponent";
import "./finance-calculator.scss";
import {
  UI_AUDI,
  DEFAULT_THEME
} from "../../constants";

class FinancePriceCalculator extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      locState: "",
      locPostcode: "",
      hasTrackedPostcode: false,
      prices: {},
      selectedVariant: null,
      selectedEngine: null,
      isPerMonth: false,
      isLoading: false,
      summary: null,
      copy: null,
      showEnquiry: false,
      showApplyModal: false,
      hasClickedVehicleDisclaimer: false
    };
  }

  componentDidMount() {
    gtmPageview(this.props.location.pathname);
  }

  render() {
    const query = queryString.parse(this.props.location.search);
    const {locPostcode, prices, selectedVariant, selectedEngine, locState, summary, isPerMonth, isLoading} = this.state;
    const theme = query.theme || DEFAULT_THEME;
    
    const loading = this.props.isFetchingModels || this.state.isFetchingEngines || this.state.isFetchingPrices || isLoading;
    return (
      <Layout ui={query.ui ? query.ui : UI_AUDI} theme={query.theme}>
        <Helmet>
          <title>Price Calculator | Audi Australia</title>
        </Helmet>

        <main className="audi-forms__page finance-price-calculator pb-0">

          {loading && <div className="spinner-wrapper sticky"><Spinner isContinuous /></div>}

          <div className={cn({"d-none": this.state.showEnquiry})}>
            <div className="row mx-0 mx-medium-n1">
              <div className="col-12 col-medium-6 px-1">
                <div className={`${theme === "dark" ? "aui-color-gray10" : "aui-color-gray95"} p-3 p-large-5 h-100`}>
                  <PriceCalculatorComponent
                    onChange={(p,v,e)=>{
                      // console.log("onchange", p, v, e);
                      this.setState({prices: p, selectedVariant: v, selectedEngine: e});
                      if (v || e) {
                        gtmPush("fpcVehicleSelected", "FinancePriceCalculator", `${v?.label || ""} ${e?.label || ""}`);
                      }
                    }}
                    locState={this.state.locState}
                    heading="Select your vehicle"
                    subheading={<div className="aui-small postcode">
                      Postcode: {locPostcode}. <button type="button" className="aui-textlink" onClick={()=>{ this.setState({locPostcode: "", locState: ""}); gtmPush("fpcChangePostcodeClicked", "FinancePriceCalculator") }}>Change?</button>
                    </div>}
                    disclaimersLabel="Vehicle disclaimers"
                    onClickDisclaimers={()=>{ if (!this.state.hasClickedVehicleDisclaimer) { gtmPush("fpcDisclaimersClicked", "FinancePriceCalculator", "vehicle"); this.setState({hasClickedVehicleDisclaimer: true}); } }}
                    theme={theme}
                  />
                </div>
              </div>
            
              <div className="col-12 col-medium-6 px-1">
                <div className={`${theme === "dark" ? "aui-color-gray10" : "aui-color-gray95"} p-3 p-large-5 h-100`}>
                  <AFSCalculatorComponent
                    engine={selectedEngine}
                    amount={prices?.VehicleDriveAwayPrice}
                    frequency={isPerMonth ? "Monthly" : "Weekly"}
                    onCalculateRepayment={(s,c) => {
                      let monthly = s?.Rate?.label?.indexOf("Monthly") > -1;
                      this.setState({summary: s, copy: c, isPerMonth: monthly});
                      let el = document.getElementById("summaryscrollpoint");
                      el.scrollIntoView(false);
                    }}
                    setIsLoading={(v)=>{ this.setState({isLoading: v}); }}
                    theme={theme}
                  />
                </div>
              </div>
            </div>
                  
            <div className="mt-5 pb-5 px-3 ctas">
              <Button
                variant="secondary"
                className="d-block d-xsmall-inline-block"
                disabled={!summary || isLoading}
                onClick={()=>{
                  this.setState({showApplyModal: true});
                  gtmPush("fpcFinanceEnquiryCTA", "FinancePriceCalculator", `${selectedVariant?.label || ""} ${selectedEngine?.label || ""}`);
                }}
              >Finance enquiry</Button>
              <Button
                variant="primary"
                className="d-block d-xsmall-inline-block"
                disabled={!summary || isLoading}
                onClick={()=>{
                  this.setState({showEnquiry: true});
                  setTimeout(() => {
                    let el = document.getElementById("enquiryscrollpoint");
                    el.scrollIntoView();
                  }, 500);
                  gtmPush("fpcVehicleEnquiryCTA", "FinancePriceCalculator", `${selectedVariant?.label || ""} ${selectedEngine?.label || ""}`);
                }}
              >Vehicle enquiry</Button>
            </div>

            <div className={`${theme === "dark" ? "aui-color-gray20" : "aui-color-gray40"} aui-color-text-light mt-2 p-3 p-large-5 price-panel`}>
              <div className="row">
                <div className="col px-medium-5">
                  {(!summary || isLoading) && <>
                    <span className="price-amount">--%</span>
                    <span className="price-amount__label aui-small">&nbsp;</span>
                  </>}
                  {summary?.ComparisonRate && !isLoading && <>
                    <span className="price-amount">{summary?.ComparisonRate?.value}{summary?.ComparisonRate?.units}</span>
                    <span className="price-amount__label aui-small">{summary?.ComparisonRate?.label}</span>
                  </>}
                  {summary?.AnnualRate && !isLoading && <>
                    <span className="price-amount">{summary?.AnnualRate?.value}{summary?.AnnualRate?.units}</span>
                    <span className="price-amount__label aui-small">{summary?.AnnualRate?.label.replace("±", "#")}</span>
                  </>}
                </div>
                <div className="col px-medium-5">
                  {(!summary || isLoading) && <>
                    <span className="price-amount">$--</span>
                    <span className="price-amount__label aui-small">&nbsp;</span>
                  </>}
                  {summary && !isLoading && <>
                    <span className="price-amount">{summary?.Rate?.units}{summary?.Rate?.value}</span>
                    <span className="price-amount__label aui-small">{summary?.Rate?.label}</span>
                  </>}
                </div>
                <div className="col px-medium-5">
                  {(!summary || isLoading) && <>
                    <span className="price-amount">$--</span>
                    <span className="price-amount__label aui-small">&nbsp;</span>
                  </>}
                  {summary?.GuaranteedFutureValue && !isLoading && <>
                    <span className="price-amount">{summary.GuaranteedFutureValue.units}{summary.GuaranteedFutureValue.value}</span>
                    <span className="price-amount__label aui-small">{summary.GuaranteedFutureValue.label}</span>
                  </>}
                  {summary?.Balloon && !isLoading && <>
                    <span className="price-amount">{summary.BalloonAmount.units}{summary.BalloonAmount.value}</span>
                    <span className="price-amount__label aui-small">{summary.BalloonAmount.label}≠</span>
                  </>}
                </div>
              </div>
              <div className="mt-4">
                <Switch
                  leftLabel="Per month"
                  rightLabel="Per week"
                  value={!this.state.isPerMonth}
                  onChange={(v)=>{ this.setState({isPerMonth: !v}); gtmPush("fpcMonthWeekSwitch", "FinancePriceCalculator", v ? "Per week" : "Per month"); }}
                  theme="light"
                />
              </div>
            </div>
          </div>

          <div id="summaryscrollpoint" style={{order:"10"}}></div>

          <div style={{order:"11"}}>
            <div id="enquiryscrollpoint"></div>
            <EnquiryForm
              queryString={query}
              locPostcode={this.state.locPostcode}
              show={this.state.showEnquiry}
              onCancel={()=>{
                this.setState({showEnquiry: false});
                setTimeout(() => {
                  let el = document.getElementById("summaryscrollpoint");
                  el.scrollIntoView(false);
                }, 500);
                gtmPush("fpcCancelClicked", "FinancePriceCalculatorEnquiry");
              }}
              data={{
                formName: "Finance Calculator",
                vehicleImageUrl: prices?.ImagePath,
                modelName: selectedVariant?.label,
                engine: selectedEngine?.label,
                loanType: this.state.copy?.loanType,
                loanOption: summary?.FinanceProduct?.value || "",
                disclaimer: this.state.copy?.productDisclaimer,
                description: this.state.copy?.description,
                offerDetails: this.state.copy?.offer || "",
                vehiclePrice: prices?.VehicleDriveAwayPrice,
                repayment: `${summary?.Rate?.units}${summary?.Rate?.value}`,
                repaymentLabel: `${summary?.Rate?.label}`,
                netAmountFinanced: `${summary?.NAF?.units}${summary?.NAF?.value}`,
                comparisonRate: summary?.AnnualRate ? `${summary.AnnualRate.value}${summary.AnnualRate.units}` : `${summary?.ComparisonRate?.value}${summary?.ComparisonRate?.units}`,
                loanTerm: summary?.Term?.value,
                deposit: `${summary?.DownPayment?.units}${summary?.DownPayment?.value}`,
                kmLimit: `${summary?.KMLimit?.value}${summary?.KMLimit?.units}`,
                gfv: `${summary?.GuaranteedFutureValue?.units}${summary?.GuaranteedFutureValue?.value}`
              }}
              thankyouDisclaimer={this.state.copy?.productDisclaimer}
              trackingFormName="FinancePriceCalculatorEnquiry"
              trackingPrefix="fpc"
              theme={theme}
            />
          </div>
                  
          <PostcodeModal isActive={!locState} onSubmit={(s,p) => {
            if (this.state.hasTrackedPostcode) {
              gtmPush("fpcPostcodeChanged", "FinancePriceCalculator", p);
            } else {
              gtmPush("fpcPostcodeLoaded", "FinancePriceCalculator", p);
            }
            this.setState({locState: s, locPostcode: p, hasTrackedPostcode: true});
          }} theme={theme} />

          <ApplyFormModal
            isActive={this.state.showApplyModal}
            close={() => { this.setState({showApplyModal: false}); }}
            variant={selectedVariant}
            engine={selectedEngine}
            image={prices?.ImagePath}
            interestRate={summary?.ComparisonRate ? summary?.ComparisonRate.value : summary?.AnnualRate?.value}
            price={summary?.DriveawayPrice.value}
            term={summary?.Term.value}
            downPayment={summary?.DownPayment.value}
            repayment={summary?.Rate.value}
            frequency={isPerMonth ? "MONTHLY" : "WEEKLY"}
            balloon={summary?.BalloonAmount?.value}
            gfv={summary?.GuaranteedFutureValue?.value}
            kmLimit={summary?.KMLimit?.value}
            disclaimer={this.state.copy?.productDisclaimer}
            description={this.state.copy?.description}
            loanType={this.state.copy?.purpose === "P" ? "ConsumerLoan" : "ChattelMortgage"}
            onEnterPostcode={(pc) => { gtmPush("fpcFinanceEnquiryPostcodeEntered", "FinancePriceCalculator", pc); }}
            onSelectDealer={(d) => { gtmPush("fpcFinanceEnquiryDealerSelected", "FinancePriceCalculator", d.dealer.name); }}
            onSuccess={()=>{ gtmPush("fpcFinanceEnquiryFinalSelectSuccess", "FinancePriceCalculator"); }}
            theme={theme}
          />

        </main>
      </Layout>
    );
  }
}

export default FinancePriceCalculator;
